import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';

import IconAlignLeft from '@/assets/icons/icon-align-left.svg?react';
import IconAlignLeftFilled from '@/assets/icons/icon-align-left-filled.svg?react';
import IconAlignCenter from '@/assets/icons/icon-align-horizontal-center.svg?react';
import IconAlignCenterFilled from '@/assets/icons/icon-align-horizontal-center-filled.svg?react';
import IconAlignRight from '@/assets/icons/icon-align-right.svg?react';
import IconAlignRightFilled from '@/assets/icons/icon-align-right-filled.svg?react';

interface ContentAlignmentHorizontalProps {
  text?: string;
  align?: string;
  onClick?: (size: string) => void;
  className?: string;
}

const aligns: Array<string> = ['left', 'center', 'right'];

export const ContentAlignmentHorizontal: React.FC<ContentAlignmentHorizontalProps> = ({
  align = 'left',
  onClick,
  className,
  text = 'Align',
}) => {
  const [selectedSize, setSelectedSize] = useState<string>(align);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    left: null,
    center: null,
    right: null,
  });

  useEffect(() => {
    if (align !== selectedSize) {
      setSelectedSize(align);
    }
  }, [align]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedSize];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedSize]);

  const handleClick = (newSize: string) => {
    setIsAnimated(true);
    setSelectedSize(newSize);
    if (onClick) onClick(newSize);
  };

  return (
    <div className={clsx('flex items-center gap-4', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <div className="relative ml-auto flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={clsx(
            'absolute top-0 z-20 h-full rounded-md bg-[#fff] outline outline-offset-[-1px] outline-[#e9ecf0]',
            isAnimated ? 'transition-all duration-300' : '',
          )}
          style={{
            width: highlightStyle.width,
            transform: `translateX(${highlightStyle.left}px)`,
          }}
        />

        {aligns.map((sz) => {
          let Icon;
          if (sz === 'left') {
            Icon = selectedSize === sz ? IconAlignLeftFilled : IconAlignLeft;
          } else if (sz === 'center') {
            Icon = selectedSize === sz ? IconAlignCenterFilled : IconAlignCenter;
          } else {
            Icon = selectedSize === sz ? IconAlignRightFilled : IconAlignRight;
          }
          const iconClass = selectedSize === sz ? 'text-indigo-500' : 'text-neutral-400';
          return (
            <button
              key={sz}
              ref={(el) => {
                optionRefs.current[sz] = el;
              }}
              onClick={() => handleClick(sz)}
              className="relative z-20 flex h-8 w-8 cursor-pointer items-center justify-center"
              type="button"
            >
              <Icon className={`h-4.5 w-4.5 ${iconClass}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};
